import {isFunction, isString} from 'lodash';

import {buildWindow, globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {
  annotateTaskStep,
  annotateTaskType,
  TaskTypesEnum,
} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {AppStore} from '../../../src/store/appStore';
import {eventLoopLogger} from './eventLoopPerformance';
import {getShouldEnableDeepPerformanceInstrumentation} from './performanceMetricSelectors';

/*
 * Types.
 */

declare global {
  interface EventTarget {
    frontOriginalAddEventListener: EventTarget['addEventListener'];
    frontOriginalRemoveEventListener: EventTarget['removeEventListener'];
  }

  interface Window {
    frontOriginalRequestAnimationFrame: Window['requestAnimationFrame'];
    frontOriginalRequestIdleCallback: Window['requestIdleCallback'];
    frontOriginalSetTimeout: Window['setTimeout'];
    frontOriginalSetInterval: Window['setInterval'];
  }
}

/*
 * Constants.
 */

const listenerMap = new WeakMap();

/*
 * State.
 */

let isSetup = false;

/*
 * Api.
 */

export function setupDeepPerformanceInstrumentation(store: AppStore) {
  if (!getShouldEnableDeepPerformanceInstrumentation(store.getState())) {
    // The user is not eligible for deep performance instrumentation.
    return;
  }

  if (isMobile()) {
    // TODO: Support mobile.
    return;
  }

  if (isSetup) {
    // We've already setup deep instrumentation.
    return;
  }

  isSetup = true;

  // Setup event target listeners.
  EventTarget.prototype.frontOriginalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.frontOriginalRemoveEventListener = EventTarget.prototype.removeEventListener;

  EventTarget.prototype.addEventListener = function frontAddEventListener(type, listener, options) {
    const wrappedListener = listener === null ? null : findOrBuildWrappedListener(listener);
    EventTarget.prototype.frontOriginalAddEventListener.call(this, type, wrappedListener, options);
  };

  EventTarget.prototype.removeEventListener = function frontRemoveEventListener(type, listener, options) {
    const wrappedListener = listener === null ? null : findWrappedListener(listener);
    EventTarget.prototype.frontOriginalRemoveEventListener.call(this, type, wrappedListener, options);

    // Try to remove the original listener since it may have been added before setting up instrumentation.
    EventTarget.prototype.frontOriginalRemoveEventListener.call(this, type, listener, options);
  };

  const nativeWindow = buildWindow(globalWindow);
  nativeWindow.frontOriginalRequestAnimationFrame = nativeWindow.requestAnimationFrame;
  nativeWindow.frontOriginalRequestIdleCallback = nativeWindow.requestIdleCallback;
  nativeWindow.frontOriginalSetTimeout = nativeWindow.setTimeout;
  nativeWindow.frontOriginalSetInterval = nativeWindow.setInterval;

  nativeWindow.requestAnimationFrame = function frontRequestAnimationFrame(callback) {
    return nativeWindow.frontOriginalRequestAnimationFrame.call(
      this,
      (...args: Parameters<FrameRequestCallback>) => {
        annotateTaskType(TaskTypesEnum.WINDOW_ANIMATION_FRAME);
        return callback(...args);
      },
    );
  };

  nativeWindow.requestIdleCallback = nativeWindow.frontOriginalRequestIdleCallback
    ? function frontRequestIdleCallback(callback, options) {
        return nativeWindow.frontOriginalRequestIdleCallback.call(
          // @ts-expect-error: The type for this is unknown.
          this,
          (...args: Parameters<IdleRequestCallback>) => {
            annotateTaskType(TaskTypesEnum.WINDOW_IDLE_CALLBACK);
            return callback(...args);
          },
          options,
        );
      }
    : nativeWindow.frontOriginalRequestIdleCallback;

  nativeWindow.setTimeout = function frontSetTimeout(timerHandler, timeout, ...args) {
    if (!isFunction(timerHandler)) {
      return nativeWindow.frontOriginalSetTimeout.call(this, timerHandler, timeout, ...args);
    }

    return nativeWindow.frontOriginalSetTimeout.call(
      this,
      (...timerHandlerArgs: ReadonlyArray<any>) => {
        annotateTaskType(TaskTypesEnum.WINDOW_TIMEOUT);
        return timerHandler(...timerHandlerArgs);
      },
      timeout,
      ...args,
    );
  };

  nativeWindow.setInterval = function frontSetInterval(timerHandler, timeout, ...args) {
    if (isString(timerHandler)) {
      return nativeWindow.frontOriginalSetInterval.call(this, timerHandler, timeout, ...args);
    }
    return nativeWindow.frontOriginalSetInterval.call(
      this,
      (...timerHandlerArgs: ReadonlyArray<any>) => {
        annotateTaskType(TaskTypesEnum.WINDOW_INTERVAL);
        return timerHandler(...timerHandlerArgs);
      },
      timeout,
      ...args,
    );
  };
}

/** Resets deep instrumentation. Exported for testing. */
export function resetDeepPerformanceInstrumentation() {
  if (!isSetup) {
    return;
  }

  isSetup = false;

  // Reset event target listeners.
  EventTarget.prototype.addEventListener = EventTarget.prototype.frontOriginalAddEventListener;
  EventTarget.prototype.removeEventListener = EventTarget.prototype.frontOriginalRemoveEventListener;

  // Reset window event schedulers.
  const nativeWindow = buildWindow(globalWindow);
  nativeWindow.requestAnimationFrame = nativeWindow.frontOriginalRequestAnimationFrame;
  nativeWindow.requestIdleCallback = nativeWindow.frontOriginalRequestIdleCallback;
  nativeWindow.setTimeout = nativeWindow.frontOriginalSetTimeout;
  nativeWindow.setInterval = nativeWindow.frontOriginalSetInterval;
}

/*
 * Helpers.
 */

function findWrappedListener(
  listener: EventListenerOrEventListenerObject,
): EventListenerOrEventListenerObject {
  return listenerMap.get(listener);
}

function findOrBuildWrappedListener(
  listener: EventListenerOrEventListenerObject,
): EventListenerOrEventListenerObject {
  if (listenerMap.has(listener)) {
    return listenerMap.get(listener);
  }

  function wrappedListener(event: Event) {
    annotateEvent(event);

    if (isFunction(listener)) {
      // @ts-expect-error: this is typed as the event target.
      listener.bind(this)(event);
    } else {
      listener.handleEvent(event);
    }
  }
  listenerMap.set(listener, wrappedListener);
  return wrappedListener;
}

function annotateEvent(event: Event) {
  try {
    annotateTaskType(TaskTypesEnum.EVENT_LISTENER);
    if (event instanceof MessageEvent) {
      annotateTaskStep('message received', {origin: event.origin});
    } else {
      const targetName = event.target?.constructor?.name || 'unknown target';
      const targetClass = event.target instanceof HTMLElement ? event.target.className : 'unknown class';
      annotateTaskStep('event listener', {type: event.type, targetName, targetClass});
    }
  } catch (error) {
    eventLoopLogger.error('Deep instrumentation error', {
      event_type: event?.type,
      error_message: error?.message,
    });
  }
}
